import { CountrySelector } from '@/components/common/CountrySelector';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Control } from 'react-hook-form';
import { FormFieldWrapper } from '../../../FormFieldWrapper';
import { MIN_BIRTH_YEAR } from '../constants';
import { IdentificationFormData } from '../types';

interface ExecutiveCardProps {
  control: Control<IdentificationFormData>;
  index: number;
  onRemove: () => void;
  canRemove: boolean;
}

export function ExecutiveCard({
  control,
  index,
  onRemove,
  canRemove,
}: ExecutiveCardProps) {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-4 border rounded-lg p-4'>
      <div className='flex justify-between items-center mb-4'>
        <h5 className='text-sm font-medium'>
          {tForms('identification.labels.executiveNumber', {
            number: index + 1,
          })}
        </h5>
        {canRemove && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onRemove}
            className='text-destructive hover:text-destructive'
          >
            <Trash2 className='w-4 h-4 mr-1' />
            {tForms('identification.buttons.remove')}
          </Button>
        )}
      </div>

      {/* Executive Details Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`executives.${index}.lastName`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executiveLastName')}
              name={`executives.${index}.lastName`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms(
                  'identification.placeholders.executiveLastName'
                )}
                className='min-h-[44px]'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`executives.${index}.firstName`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executiveFirstName')}
              name={`executives.${index}.firstName`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms(
                  'identification.placeholders.executiveFirstName'
                )}
                className='min-h-[44px]'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 gap-4 items-start'>
        <FormField
          control={control}
          name={`executives.${index}.address`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executiveAddress')}
              name={`executives.${index}.address`}
              required
              error={fieldState.error?.message}
              tooltip={tForms('identification.tooltips.executiveAddress')}
            >
              <Input
                {...field}
                placeholder={tForms(
                  'identification.placeholders.executiveAddress'
                )}
                className='min-h-[44px]'
                autoComplete='street-address'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`executives.${index}.postcode`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executivePostcode')}
              name={`executives.${index}.postcode`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms(
                  'identification.placeholders.executivePostcode'
                )}
                className='min-h-[44px]'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`executives.${index}.city`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executiveCity')}
              name={`executives.${index}.city`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms(
                  'identification.placeholders.executiveCity'
                )}
                className='min-h-[44px]'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`executives.${index}.country`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executiveCountry')}
              name={`executives.${index}.country`}
              required
              error={fieldState.error?.message}
            >
              <CountrySelector
                value={field.value}
                onValueChange={field.onChange}
                placeholder={tForms(
                  'identification.placeholders.executiveCountry'
                )}
                error={fieldState.error?.message}
                required
                name={`executives.${index}.country`}
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`executives.${index}.position`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executivePosition')}
              name={`executives.${index}.position`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms(
                  'identification.placeholders.executivePosition'
                )}
                className='min-h-[44px]'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`executives.${index}.birthDate`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executiveBirthDate')}
              name={`executives.${index}.birthDate`}
              required
              error={fieldState.error?.message}
            >
              <Input
                type='date'
                value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
                onChange={e => {
                  const date = e.target.value
                    ? new Date(e.target.value)
                    : undefined;
                  field.onChange(date);
                }}
                className='min-h-[44px]'
                max={format(new Date(), 'yyyy-MM-dd')} // Prevent future dates
                min={MIN_BIRTH_YEAR} // Reasonable minimum birth year
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`executives.${index}.nationality`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.executiveNationality')}
              name={`executives.${index}.nationality`}
              required
              error={fieldState.error?.message}
            >
              <CountrySelector
                value={field.value}
                onValueChange={field.onChange}
                placeholder={tForms(
                  'identification.placeholders.executiveNationality'
                )}
                error={fieldState.error?.message}
                required
                name={`executives.${index}.nationality`}
              />
            </FormFieldWrapper>
          )}
        />
      </div>
    </div>
  );
}
